import { useEffect, useRef, useState } from 'react';
import { browserTransport, createActivityClient, type ActivityTransport } from './client.js';
import { createActivityReconciler, type ActivityState } from './reconcile.js';

const EMPTY = createActivityReconciler().state();

/**
 * Subscribes to background work for as long as there is a session to subscribe with.
 *
 * `live` is not a convenience. The snapshot is one authenticated GET, and the shell
 * mounts before anyone has signed in: read then, it comes back 401, the client swallows
 * it, and `snapshotApplied` stays false for the rest of the session - so `activeCount`
 * is 0 forever and the indicator never appears at all. Every unit test missed it,
 * because an injected transport answers the snapshot whether there is a session or not.
 * So the client is built when the session opens and torn down when it closes.
 *
 * The transport itself is still built once per client: reopening the stream on a
 * re-render would reopen the very race the reconciler exists to close.
 */
export function useActivity(
  makeTransport: () => ActivityTransport = browserTransport,
  live = true,
): ActivityState {
  const [state, setState] = useState<ActivityState>(EMPTY);
  const factory = useRef(makeTransport);

  useEffect(() => {
    if (!live) {
      setState(EMPTY);
      return;
    }
    const client = createActivityClient(factory.current(), setState);
    return () => client.close();
  }, [live]);

  return state;
}
